import { useState, useEffect, useRef, useMemo, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Star, Upload, AlertTriangle, Plus, Trash2, ImagePlus, UserRound } from 'lucide-react';
import { candidateService } from '@core/supabase/services/candidateService';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';
import { CandidateResumePreviewButton } from './CandidateResumePreviewButton';
import { PrivateStorageAvatar } from '@shared/components/ui/PrivateStorageAvatar';
import { Candidate, SOURCE_CHANNELS, CandidateSource, CANDIDATE_SOURCE_LABELS, type EducationEntry } from '@core/types';

/** Module-level: avoids remounting inputs every parent render (one-character typing). */
function CandidateFormRow({
  label,
  children,
  span = 1,
}: {
  label: ReactNode;
  children: React.ReactNode;
  span?: 1 | 2;
}) {
  return (
    <div
      className={`grid grid-cols-[minmax(130px,155px)_1fr] gap-x-3 items-start border-b border-[var(--vscode-border-divider)] py-[0.65rem] last:border-b-0 ${
        span === 2 ? 'md:col-span-2' : ''
      }`}
    >
      <span className="text-sm vscode-text-secondary leading-snug pt-1.5 pr-2">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CandidateFormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <div className="ggj-profile-section-h">{title}</div>
      <div className="ggj-profile-section-body">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">{children}</div>
      </div>
    </>
  );
}

export interface CandidateFormProps {
  mode: 'new' | 'edit';
  candidate?: Candidate | null;
  /** When true, form is read-only (view mode). When false, editable. */
  readOnly?: boolean;
  onSave: (candidate: Candidate) => void;
  onDelete?: () => void;
  orgId: string;
  userId: string;
  /** Override when embedding (e.g. role fit drill-down). */
  formId?: string;
}

export function CandidateForm({
  mode,
  candidate,
  readOnly = false,
  onSave,
  onDelete,
  orgId,
  userId,
  formId = 'candidate-form',
}: CandidateFormProps) {
  const disabled = readOnly;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState<Candidate[]>([]);
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [notesAiOpen, setNotesAiOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: candidate?.full_name ?? '',
    email: candidate?.email ?? '',
    phone: candidate?.phone ?? '',
    linkedin_url: candidate?.linkedin_url ?? '',
    source_channel: (candidate?.source_channel as string) ?? '',
    source: (candidate?.source ?? '') as CandidateSource | '',
    utm_source: candidate?.utm_source ?? '',
    utm_medium: candidate?.utm_medium ?? '',
    utm_campaign: candidate?.utm_campaign ?? '',
    current_company: candidate?.current_company ?? '',
    current_title: candidate?.current_title ?? '',
    location: candidate?.location ?? '',
    years_exp: candidate?.years_exp != null ? String(candidate.years_exp) : '',
    tags: (candidate?.tags ?? []).join(', '),
    notes: candidate?.notes ?? '',
    recruiter_rating: candidate?.recruiter_rating != null ? String(candidate.recruiter_rating) : '',
  });

  const [educationRows, setEducationRows] = useState<
    { degree: string; institution: string; year: string }[]
  >(() =>
    candidate?.education?.length
      ? candidate.education.map((e) => ({
          degree: e.degree ?? '',
          institution: e.institution ?? '',
          year: e.year != null ? String(e.year) : '',
        }))
      : []
  );

  useEffect(() => {
    setAvatarFile(null);
    setRemoveAvatar(false);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  }, [candidate?.id, mode]);

  /** Hydrate from server in view mode only — never overwrite local state while Edit is active (avoids polling / updated_at flicker). */
  useEffect(() => {
    if (!candidate) return;
    if (!readOnly) return;
    setForm({
      full_name: candidate.full_name ?? '',
      email: candidate.email ?? '',
      phone: candidate.phone ?? '',
      linkedin_url: candidate.linkedin_url ?? '',
      source_channel: (candidate.source_channel as string) ?? '',
      source: (candidate.source ?? '') as CandidateSource | '',
      utm_source: candidate.utm_source ?? '',
      utm_medium: candidate.utm_medium ?? '',
      utm_campaign: candidate.utm_campaign ?? '',
      current_company: candidate.current_company ?? '',
      current_title: candidate.current_title ?? '',
      location: candidate.location ?? '',
      years_exp: candidate.years_exp != null ? String(candidate.years_exp) : '',
      tags: (candidate.tags ?? []).join(', '),
      notes: candidate.notes ?? '',
      recruiter_rating: candidate.recruiter_rating != null ? String(candidate.recruiter_rating) : '',
    });
    setEducationRows(
      candidate.education?.length
        ? candidate.education.map((e) => ({
            degree: e.degree ?? '',
            institution: e.institution ?? '',
            year: e.year != null ? String(e.year) : '',
          }))
        : []
    );
  }, [candidate?.id, candidate?.updated_at, readOnly, candidate]);

  const educationPayload = (): EducationEntry[] | null => {
    const rows = educationRows
      .map((r) => ({
        degree: r.degree.trim(),
        institution: r.institution.trim(),
        year: r.year.trim() === '' ? null : parseInt(r.year, 10),
      }))
      .filter((r) => r.degree || r.institution)
      .map((r) => ({
        ...r,
        year: r.year != null && !Number.isNaN(r.year) ? r.year : null,
      }));
    return rows.length ? rows : null;
  };

  const avatarObjectUrl = useMemo(() => {
    if (!avatarFile) return null;
    return URL.createObjectURL(avatarFile);
  }, [avatarFile]);

  useEffect(() => {
    return () => {
      if (avatarObjectUrl) URL.revokeObjectURL(avatarObjectUrl);
    };
  }, [avatarObjectUrl]);

  const set = (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) { setError('Full name is required'); return; }
    if (!form.email.trim()) { setError('Email is required'); return; }

    setLoading(true);
    setError('');
    try {
      if (mode === 'new') {
        const created = await candidateService.create({
          org_id: orgId,
          created_by: userId,
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          phone: form.phone || null,
          linkedin_url: form.linkedin_url || null,
          source_channel: form.source_channel || null,
          source: (form.source || null) as CandidateSource | null,
          utm_source: form.utm_source || null,
          utm_medium: form.utm_medium || null,
          utm_campaign: form.utm_campaign || null,
          current_company: form.current_company || null,
          current_title: form.current_title || null,
          location: form.location || null,
          avatar_url: null,
          base_resume_url: null,
          parsed_skills: null,
          years_exp: form.years_exp ? parseInt(form.years_exp) : null,
          education: educationPayload(),
          tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
          notes: form.notes || null,
          recruiter_rating: form.recruiter_rating ? parseInt(form.recruiter_rating, 10) : null,
          is_active: true,
        });
        if (avatarFile) {
          const ap = await candidateService.uploadAvatar(orgId, created.id, avatarFile);
          await candidateService.update(created.id, { avatar_url: ap });
        }
        if (resumeFile) {
          const path = await candidateService.uploadResume(orgId, created.id, resumeFile);
          await candidateService.update(created.id, { base_resume_url: path });
        }
        const after = await candidateService.getById(created.id);
        onSave(after ?? created);
      } else if (candidate) {
        const updated = await candidateService.update(candidate.id, {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          phone: form.phone || null,
          linkedin_url: form.linkedin_url || null,
          source_channel: form.source_channel || null,
          source: (form.source || null) as CandidateSource | null,
          utm_source: form.utm_source || null,
          utm_medium: form.utm_medium || null,
          utm_campaign: form.utm_campaign || null,
          current_company: form.current_company || null,
          current_title: form.current_title || null,
          location: form.location || null,
          years_exp: form.years_exp ? parseInt(form.years_exp) : null,
          education: educationPayload(),
          tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
          notes: form.notes || null,
          recruiter_rating: form.recruiter_rating ? parseInt(form.recruiter_rating, 10) : null,
          ...(removeAvatar && !avatarFile ? { avatar_url: null } : {}),
        });
        let final = updated;
        if (avatarFile) {
          const ap = await candidateService.uploadAvatar(orgId, candidate.id, avatarFile);
          final = await candidateService.update(candidate.id, { avatar_url: ap });
        }
        if (resumeFile) {
          const path = await candidateService.uploadResume(orgId, candidate.id, resumeFile);
          final = await candidateService.update(candidate.id, { base_resume_url: path });
        }
        onSave(final);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const triggerDupCheck = (email: string, phone: string) => {
    if (disabled || mode === 'edit') return;
    if (dupCheckTimer.current) clearTimeout(dupCheckTimer.current);
    dupCheckTimer.current = setTimeout(async () => {
      const found = await candidateService.checkDuplicates(orgId, {
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        excludeId: candidate?.id,
      });
      setDuplicates(found);
    }, 400);
  };

  return (
    <>
    <form id={formId} onSubmit={handleSubmit} className="">
      {/* CAN-040: duplicate warning */}
      {duplicates.length > 0 && (
        <div className="mb-4 flex gap-2 rounded-lg border border-amber-700/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
          <div>
            <p className="font-medium text-amber-300">Possible duplicate detected</p>
            <ul className="mt-1 space-y-0.5">
              {duplicates.map((d) => (
                <li key={d.id}>
                  <Link to={`/candidates/${d.id}`} className="underline hover:text-white">
                    {d.full_name}
                  </Link>
                  {d.email ? ` · ${d.email}` : ''}
                  {d.current_title ? ` · ${d.current_title}` : ''}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-amber-100/60 text-xs">Review the existing record before saving a new one.</p>
          </div>
        </div>
      )}
      {/* Basic Information */}
      <CandidateFormSection title="Basic Information">
        <CandidateFormRow label="Profile photo" span={2}>
          <div className="flex flex-wrap items-start gap-4">
            <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-[color-mix(in_srgb,var(--vscode-primary)_22%,var(--vscode-border-primary))] shrink-0 bg-[var(--vscode-bg-secondary)] flex items-center justify-center">
              {avatarObjectUrl ? (
                <img src={avatarObjectUrl} alt="" className="w-full h-full object-cover" />
              ) : mode === 'edit' && candidate?.avatar_url && !removeAvatar ? (
                <PrivateStorageAvatar
                  path={candidate.avatar_url}
                  className="w-full h-full object-cover"
                  fallback={<UserRound className="w-12 h-12 opacity-45 vscode-text-tertiary" aria-hidden />}
                />
              ) : (
                <UserRound className="w-12 h-12 opacity-45 vscode-text-tertiary" aria-hidden />
              )}
            </div>
            {!disabled && (
              <div className="flex flex-col gap-2 min-w-0">
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setAvatarFile(f ?? null);
                    setRemoveAvatar(false);
                  }}
                />
                <button
                  type="button"
                  className="btn-secondary text-xs inline-flex items-center gap-1.5 w-fit"
                  onClick={() => avatarInputRef.current?.click()}
                >
                  <ImagePlus className="w-3.5 h-3.5 gg-toolbar-ic gg-toolbar-ic--primary" aria-hidden />
                  Upload photo
                </button>
                {mode === 'edit' && candidate?.avatar_url && !avatarObjectUrl ? (
                  <button
                    type="button"
                    className="text-xs text-red-600 dark:text-red-400 hover:underline w-fit text-left"
                    onClick={() => {
                      setRemoveAvatar(true);
                      setAvatarFile(null);
                      if (avatarInputRef.current) avatarInputRef.current.value = '';
                    }}
                  >
                    Remove current photo
                  </button>
                ) : null}
                <p className="text-[11px] vscode-text-tertiary leading-snug max-w-md">
                  JPG, PNG, GIF or WebP. Shown in At a glance and lists when configured.
                </p>
              </div>
            )}
          </div>
        </CandidateFormRow>
        <CandidateFormRow label="Full Name *">
          <input className="input w-full" placeholder="Rahul Sharma" value={form.full_name} onChange={set('full_name')} disabled={disabled} />
        </CandidateFormRow>
        <CandidateFormRow label="Email *">
          <input
            className="input w-full"
            type="email"
            placeholder="rahul@example.com"
            value={form.email}
            onChange={set('email')}
            onBlur={(e) => triggerDupCheck(e.target.value, form.phone)}
            disabled={disabled}
          />
        </CandidateFormRow>
        <CandidateFormRow label="Phone">
          <input
            className="input w-full"
            placeholder="+91 98765 43210"
            value={form.phone}
            onChange={set('phone')}
            onBlur={(e) => triggerDupCheck(form.email, e.target.value)}
            disabled={disabled}
          />
        </CandidateFormRow>
        <CandidateFormRow label="LinkedIn URL">
          <input className="input w-full" placeholder="linkedin.com/in/rahul" value={form.linkedin_url} onChange={set('linkedin_url')} disabled={disabled} />
        </CandidateFormRow>
        <CandidateFormRow label="Source Channel">
          <select className="input w-full" value={form.source_channel} onChange={(e) => setForm((f) => ({ ...f, source_channel: e.target.value }))} disabled={disabled}>
            <option value="">Select source...</option>
            {SOURCE_CHANNELS.map((ch) => (
              <option key={ch} value={ch}>{ch}</option>
            ))}
          </select>
        </CandidateFormRow>
        <CandidateFormRow label="Source (structured)">
          <select className="input w-full" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value as CandidateSource | '' }))} disabled={disabled}>
            <option value="">— none —</option>
            {(Object.entries(CANDIDATE_SOURCE_LABELS) as [CandidateSource, string][]).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </CandidateFormRow>
        {!disabled && (
          <>
            <CandidateFormRow label="utm_source">
              <input className="input w-full" placeholder="utm_source" value={form.utm_source} onChange={set('utm_source')} />
            </CandidateFormRow>
            <CandidateFormRow label="utm_medium">
              <input className="input w-full" placeholder="utm_medium" value={form.utm_medium} onChange={set('utm_medium')} />
            </CandidateFormRow>
            <CandidateFormRow label="utm_campaign">
              <input className="input w-full" placeholder="utm_campaign" value={form.utm_campaign} onChange={set('utm_campaign')} />
            </CandidateFormRow>
          </>
        )}
      </CandidateFormSection>

      {/* Professional Details */}
      <CandidateFormSection title="Professional Details">
        <CandidateFormRow label="Current Company">
          <input className="input w-full" placeholder="Infosys" value={form.current_company} onChange={set('current_company')} disabled={disabled} />
        </CandidateFormRow>
        <CandidateFormRow label="Current Title">
          <input className="input w-full" placeholder="Senior Developer" value={form.current_title} onChange={set('current_title')} disabled={disabled} />
        </CandidateFormRow>
        <CandidateFormRow label="Location">
          <input className="input w-full" placeholder="Bengaluru" value={form.location} onChange={set('location')} disabled={disabled} />
        </CandidateFormRow>
        <CandidateFormRow label="Years of Experience">
          <input className="input w-full" type="number" min="0" max="50" placeholder="4" value={form.years_exp} onChange={set('years_exp')} disabled={disabled} />
        </CandidateFormRow>
        <CandidateFormRow label="Recruiter rating (1–5)">
          <div className="flex items-center gap-1 py-1">
            {[1, 2, 3, 4, 5].map((n) => {
              const cur = form.recruiter_rating ? parseInt(form.recruiter_rating, 10) : 0;
              const active = cur >= n;
              if (disabled) {
                return <Star key={n} className={`h-6 w-6 ${active ? 'fill-amber-400 text-amber-400' : 'text-gray-700'}`} aria-hidden />;
              }
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() =>
                    setForm((f) => {
                      const existing = f.recruiter_rating ? parseInt(f.recruiter_rating, 10) : 0;
                      if (existing === n) return { ...f, recruiter_rating: '' };
                      return { ...f, recruiter_rating: String(n) };
                    })
                  }
                  className="rounded p-0.5 text-gray-500 hover:text-amber-300 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  title={`${n} star${n > 1 ? 's' : ''}`}
                >
                  <Star className={`h-6 w-6 ${active ? 'fill-amber-400 text-amber-400' : ''}`} />
                </button>
              );
            })}
            {!disabled && form.recruiter_rating ? (
              <button
                type="button"
                className="ml-2 text-xs text-gray-500 hover:text-gray-300"
                onClick={() => setForm((f) => ({ ...f, recruiter_rating: '' }))}
              >
                Clear
              </button>
            ) : null}
          </div>
        </CandidateFormRow>
        <CandidateFormRow label="Tags" span={2}>
          <input className="input w-full" placeholder="Java, Backend, Bangalore (comma separated)" value={form.tags} onChange={set('tags')} disabled={disabled} />
        </CandidateFormRow>
        <CandidateFormRow
          label={
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span>Background / internal notes</span>
              {!disabled && (
                <AiAssistIconButton onClick={() => setNotesAiOpen(true)} title="AI assist — candidate notes" />
              )}
            </span>
          }
          span={2}
        >
          <textarea className="input w-full resize-none" rows={3} placeholder="Notes visible only to your team..." value={form.notes} onChange={set('notes')} disabled={disabled} />
        </CandidateFormRow>
      </CandidateFormSection>

      {/* Education */}
      <CandidateFormSection title="Education">
        <div className="md:col-span-2 space-y-2">
          {educationRows.length === 0 && disabled ? (
            <p className="text-sm vscode-text-tertiary py-1">No education entries.</p>
          ) : null}
          {educationRows.map((row, i) => (
            <div
              key={i}
              className="flex flex-col sm:flex-row flex-wrap gap-2 sm:items-end px-2 py-1.5 rounded-md border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)]"
            >
              <input
                className="input flex-1 min-w-[140px]"
                placeholder="Degree / qualification"
                value={row.degree}
                onChange={(e) =>
                  setEducationRows((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i]!, degree: e.target.value };
                    return next;
                  })
                }
                disabled={disabled}
              />
              <input
                className="input flex-1 min-w-[140px]"
                placeholder="Institution"
                value={row.institution}
                onChange={(e) =>
                  setEducationRows((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i]!, institution: e.target.value };
                    return next;
                  })
                }
                disabled={disabled}
              />
              <input
                className="input w-24"
                type="number"
                placeholder="Year"
                min={1950}
                max={2100}
                value={row.year}
                onChange={(e) =>
                  setEducationRows((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i]!, year: e.target.value };
                    return next;
                  })
                }
                disabled={disabled}
              />
              {!disabled && (
                <button
                  type="button"
                  className="btn-secondary text-xs px-2 py-2 inline-flex items-center gap-1 shrink-0"
                  onClick={() => setEducationRows((prev) => prev.filter((_, j) => j !== i))}
                  aria-label="Remove education row"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {!disabled && (
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-1.5"
              onClick={() =>
                setEducationRows((prev) => [...prev, { degree: '', institution: '', year: '' }])
              }
            >
              <Plus className="w-3.5 h-3.5" /> Add education
            </button>
          )}
        </div>
      </CandidateFormSection>

      {/* Skills */}
      {mode === 'edit' && candidate && (candidate.parsed_skills?.length ?? 0) > 0 && (
        <CandidateFormSection title="Skills">
          <div className="md:col-span-2 flex flex-wrap gap-1.5 py-1">
            {candidate.parsed_skills!.map((s) => (
              <span
                key={s}
                className="px-2 py-0.5 text-xs rounded border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)] vscode-text-primary"
              >
                {s}
              </span>
            ))}
          </div>
          <p className="md:col-span-2 text-xs vscode-text-tertiary">Skills are parsed from résumé. Re-upload to update.</p>
        </CandidateFormSection>
      )}

      {/* Base Résumé */}
      <CandidateFormSection title="Base Résumé">
        <div className="md:col-span-2 space-y-2 py-1">
          {mode === 'edit' && candidate?.base_resume_url ? (
            <>
              <CandidateResumePreviewButton
                storagePath={candidate.base_resume_url}
                fullName={form.full_name || candidate.full_name}
                currentTitle={form.current_title || candidate.current_title}
                eyebrow="Base résumé (saved to profile)"
                label="View current résumé"
                className="text-brand-400 hover:underline font-normal"
              />
              {!disabled && <p className="text-xs text-gray-500">Upload a new file to replace:</p>}
            </>
          ) : null}
          {!disabled && (
            <label className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-[var(--vscode-border-primary)] rounded-lg cursor-pointer hover:border-[var(--vscode-primary)] transition-colors max-w-md">
              <Upload className="w-6 h-6 vscode-text-tertiary" />
              <p className="text-sm vscode-text-primary">{resumeFile ? resumeFile.name : 'Click to upload résumé (PDF or DOCX)'}</p>
              <p className="text-xs vscode-text-tertiary">Max 10MB</p>
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResumeFile(e.target.files?.[0] ?? null)} />
            </label>
          )}
        </div>
      </CandidateFormSection>

      {error && (
        <p className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>
      )}

      {/* New mode only: form footer buttons. Edit mode buttons are in header. */}
      {mode === 'new' && (
        <div className="flex gap-2 flex-wrap">
          <Link to="/candidates" className="btn-secondary">[D] Discard</Link>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : '[S] Save'}
          </button>
        </div>
      )}
    </form>

    <ContextAiAssistModal
      open={notesAiOpen}
      onClose={() => setNotesAiOpen(false)}
      orgId={orgId}
      feature="ai_assist_candidate_notes"
      preset="candidate_notes"
      title="AI — candidate notes"
      contextHint={`Name: ${form.full_name}\nRole: ${form.current_title || '—'}\nEmail: ${form.email}`}
      original={form.notes}
      onApply={(text, mode) =>
        setForm((f) => ({
          ...f,
          notes:
            mode === 'replace'
              ? text
              : f.notes.trim()
                ? `${f.notes.trim()}\n\n${text}`
                : text,
        }))
      }
    />
    </>
  );
}
